import { useVisibleEntries } from '@/components/content/FacetNavigation/hooks/useVisibleEntries';
import { FacetNavigationPrice } from '@/components/content/FacetNavigation/parts/Price';
import { FacetNavigationPriceRangePicker } from '@/components/content/FacetNavigation/parts/PriceRangePicker';
import { facetNavigationAccordionSX } from '@/components/content/FacetNavigation/styles/accordion';
import { facetNavigationAccordionDetailsSX } from '@/components/content/FacetNavigation/styles/accordionDetails';
import { facetNavigationAccordionSummarySX } from '@/components/content/FacetNavigation/styles/accordionSummary';
import { itemChangeSX } from '@/components/content/FacetNavigation/styles/itemChangeContainer';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { useLocalization } from '@/data/Localization';
import { FACET_PRICE_PREFIX } from '@/data/constants/facet';
import { ContentContext } from '@/data/context/content';
import { Box, Breakpoint, useMediaQuery } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';
import { FacetNavigationFacetElement } from '@/components/content/FacetNavigation/parts/FacetElement';

const mobileBreakpoint: Breakpoint = 'md';

const isPriceRangeValid = (minPrice: number | null, maxPrice: number | null): boolean => {
	if (minPrice === null || maxPrice === null) return true;

	return minPrice <= maxPrice;
};
const MINS = {
	min: 0,
	max: 1,
};

const availabilityOnFisicalStore = 'Disponibilidad en tienda física';
const availableInStore = 'Existencia en tienda física';

export const FacetNavigationItemChange: FC = () => {
	const productFilterNLS = useLocalization('ProductFilter');
	const { facets, filteredParams, onPriceRangeChange } = useContext(ContentContext) as ReturnType<
		typeof useFacetNavigation
	>;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
	const visibleEntries = useVisibleEntries(facets);
	const filteredMinPrice = filteredParams.minPrice ?? -1;
	const filteredMaxPrice = filteredParams.maxPrice ?? -1;

	const [maxPrice, setMaxPrice] = useState<number | null>(filteredParams.maxPrice ?? null);
	const [minPrice, setMinPrice] = useState<number | null>(filteredParams.minPrice ?? null);

	const [expanded, setExpanded] = useState<string[]>([]);
	const hasInitialized = useRef(false);

	useEffect(() => {
		if (!isMobile && facets.length > 0 && !hasInitialized.current) {
			const firstFive = facets.slice(0, 5).map((f) => f.name);
			setExpanded(firstFive);
			hasInitialized.current = true;
		}
	}, [facets, isMobile]);

	useEffect(() => {
		setMinPrice(filteredParams.minPrice ?? null);
		setMaxPrice(filteredParams.maxPrice ?? null);
	}, [filteredParams.minPrice, filteredParams.maxPrice]);

	const handleAccordionChange = useCallback(
		(panelId: string) => (_event: React.SyntheticEvent, isNowExpanded: boolean) => {
			setExpanded((prev) => {
				if (isNowExpanded) {
					return prev.includes(panelId) ? prev : [...prev, panelId];
				}

				return prev.filter((id) => id !== panelId);
			});
		},
		[]
	);

	const btnPriceDisabledMobile = useMemo(
		() => maxPrice === null || !isPriceRangeValid(minPrice, maxPrice),
		[maxPrice, minPrice]
	);

	const onSubmitPriceMobile = useCallback(() => {
		if (maxPrice !== null) {
			onPriceRangeChange({ minPrice: minPrice ?? MINS.min, maxPrice });
		}
	}, [minPrice, maxPrice, onPriceRangeChange]);

	return (
		<Box sx={itemChangeSX.container} className="product-filter">
			{facets.map((facetItem) => {
				const accordionId = `productFilter-facet-${facetItem.name}`;
				const expandedFacetValue = facetItem.name;
				const isExpanded = expanded.includes(expandedFacetValue);
				const accordionText = `${facetItem.extendedData?.fname ?? facetItem.name}`;
				return (
					<Accordion
						key={accordionId}
						id={accordionId}
						data-testid={accordionId}
						expanded={isExpanded}
						onChange={handleAccordionChange(expandedFacetValue)}
						sx={facetNavigationAccordionSX}
						disableGutters
						square
						elevation={0}
					>
						<AccordionSummary
							aria-controls={`${facetItem.value}-content`}
							id={`${facetItem.value}-header`}
							data-testid={`${facetItem.value}-header`}
							sx={facetNavigationAccordionSummarySX}
							expandIcon={
								<DynamicIcon
									name={isExpanded ? MATERIAL_SYMBOL_ICONS.remove : MATERIAL_SYMBOL_ICONS.add}
								/>
							}
						>
							<Typography variant="body1" fontWeight={600} className="product-filter__title">
								{facetItem.value.startsWith(FACET_PRICE_PREFIX)
									? productFilterNLS.Labels.price.t()
									: accordionText === availabilityOnFisicalStore
									? availableInStore
									: accordionText}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={facetNavigationAccordionDetailsSX}>
							{facetItem.value.startsWith(FACET_PRICE_PREFIX) ? (
								<>
									{!(filteredMaxPrice > -1 && filteredMinPrice > -1) ? (
										<FacetNavigationPriceRangePicker
											isMobile={isMobile}
											btnDisabled={btnPriceDisabledMobile}
											minPrice={minPrice}
											maxPrice={maxPrice}
											setMaxPrice={setMaxPrice}
											setMinPrice={setMinPrice}
											onSubmitPrice={onSubmitPriceMobile}
										/>
									) : (
										<FacetNavigationPrice />
									)}
								</>
							) : (
								<FacetNavigationFacetElement
									facetItem={facetItem}
									visibleEntries={visibleEntries}
								/>
							)}
						</AccordionDetails>
					</Accordion>
				);
			})}
		</Box>
	);
};
